import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, Form } from 'react-router'
import { z } from 'zod'
import { floatingToolbarClassName } from '#app/components/floating-toolbar.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { type Route } from './+types/active.tsx'

export { action } from './__schedules-editor.server.tsx'

export const ActiveSchedulesSchema = z.object({
	current: z.string().nullish(),
	previous: z.string().nullish(),
	signup: z.string().nullish(),
})

export async function loader() {
	const empty = { id: '', date: '', state: '' }
	const select = { select: { id: true, date: true, state: true } }
	const activeSchedules = await prisma.activeSchedules.findFirst({
		select: {
			currentSchedule: select,
			previousSchedule: select,
			signupSchedule: select,
		},
	})
	return data({
		current: activeSchedules?.currentSchedule ?? empty,
		previous: activeSchedules?.previousSchedule ?? empty,
		signup: activeSchedules?.signupSchedule ?? empty,
	})
}

export default function ActiveSchedulesRoute({
	loaderData: { current, previous, signup },
}: Route.ComponentProps) {
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'active-schedules',
		constraint: getZodConstraint(ActiveSchedulesSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ActiveSchedulesSchema })
		},
		defaultValue: {
			current: current.date,
			previous: previous.date,
			signup: signup.date,
		},
	})

	return (
		<div className="absolute inset-0">
			<Form
				method="POST"
				className="flex h-full flex-col gap-y-4 overflow-y-auto overflow-x-hidden px-10 pb-28 pt-12"
				{...getFormProps(form)}
				encType="multipart/form-data"
			>
				<div className="flex flex-col gap-1">
					<Field
						className="w-[250px]"
						labelProps={{ children: 'Open for Sign-up' }}
						inputProps={{
							disabled: true,
							...getInputProps(fields.signup, {
								type: 'date',
								ariaAttributes: true,
							}),
						}}
						errors={fields.signup.errors}
					/>
					<Field
						className="w-[250px]"
						labelProps={{ children: 'Current Timeline' }}
						inputProps={{
							disabled: true,
							...getInputProps(fields.current, {
								type: 'date',
								ariaAttributes: true,
							}),
						}}
						errors={fields.current.errors}
					/>
					<Field
						className="w-[250px]"
						labelProps={{ children: 'Previous Timeline' }}
						inputProps={{
							disabled: true,
							...getInputProps(fields.previous, {
								type: 'date',
								ariaAttributes: true,
							}),
						}}
						errors={fields.previous.errors}
					/>
				</div>
				<ErrorList id={form.errorId} errors={form.errors} />
			</Form>
			<div className={floatingToolbarClassName}>
				<Button form={form.id} variant="destructive" type="reset">
					Reset
				</Button>
				<StatusButton
					form={form.id}
					type="submit"
					disabled={isPending}
					status={isPending ? 'pending' : 'idle'}
				>
					Submit
				</StatusButton>
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = () => {
	return [
		{ title: `Active Irrigation Schedules | Clearwater Farms 1` },
		{
			name: 'description',
			content: `Active Irrigation Schedules for Clearwater Farms 1`,
		},
	]
}
